import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, Link, useLocation } from 'react-router-dom';
import qs from 'qs';
import toast from '~~elements/Toast';
import Button from '~~elements/Button';
import Multilingual from '~~utils/Multilingual';
import { useQuery } from '~~utils/CommonUtils';
import { GameResource, PlayerResource } from '~~apis/resource';
import { StyledDiv } from '../Settings/Styles';
import { useTournamentDispatch, useTournamentState } from './useTournament';
import {
  updateGameMedia,
  updateGameNews,
  deleteGameNews,
  updateGameRelatedLinks,
  validScoreBoardData,
  updateGameScoreBoard,
  validStatsData,
  updateGameStats,
} from './service';
import { initInGamePlayersOrder } from './Const';
import { StyledBackIcon } from './styled';
import GameOverviewSection from './components/GameOverviewSection';
import GameScoreBoardSection from './components/GameScoreBoardSection';
import GameMediaSection from './components/GameMediaSection';
import GameNewsSection from './components/GameNewsSection';
import GameRelatedLinksSection from './components/GameRelatedLinksSection';
import GameStatsSection from './components/GameStatsSection';
import GameAdminSection from './components/GameAdminSection';
import GameG66NSection from './components/GameG66NSection';
import moment from 'moment';

const Game = ({ isPlatformAdmin, organizer, game }) => {
  const query = useQuery();
  const urlLocation = useLocation();
  const history = useHistory();
  const part = query.get('part') ? query.get('part') : 'overview';

  const { tournamentInfo, games } = useTournamentState();
  const tournamentDispatch = useTournamentDispatch();
  const [gameTitle, setGameTitle] = useState('');

  const [playerLoading, setPlayerLoading] = useState(false);
  const [gameWithPlayers, setGameWithPlayers] = useState(game);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [homePlayers, setHomePlayers] = useState([]);

  const [relatedLinks, setRelatedLinks] = useState([]);

  const [editData, setEditData] = useState(null);
  const [editApiInProgress, setEditApiInProgress] = useState(false);

  useEffect(() => {
    setGameWithPlayers(game);

    const seq = `G${game.seq}`;
    const away = !game.away.team ? Multilingual(`GAME.TEAM_NONE`) : game.away.team;
    const home = !game.home.team ? Multilingual(`GAME.TEAM_NONE`) : game.home.team;
    const date = moment(game.info.time.scheduled_start_at).format('YYYY/MM/DD HH:mm');
    setGameTitle(`${seq} ${away} vs ${home} ${date}`);
  }, [game]);

  useEffect(() => {
    setPlayerLoading(true);

    let apiCountDown = 3;
    const apiDone = () => {
      apiCountDown--;
      if (apiCountDown !== 0) {
        return;
      }
      setPlayerLoading(false);
    };

    GameResource.getGame({ seasonUniqid: tournamentInfo.uniqid, gameUniqid: game.uniqid })
      .then(({ data }) => {
        setGameWithPlayers({
          ...data,
          away: {
            ...data.away,
            players: initInGamePlayersOrder(data.away.players, data.away.batters, data.away.pitchers),
          },
          home: {
            ...data.home,
            players: initInGamePlayersOrder(data.home.players, data.home.batters, data.home.pitchers),
          },
        });
        apiDone();
      })
      .catch(() => {});

    const awayTeamUniqid = game.away.season_team_uniqid;
    if (!awayTeamUniqid) {
      apiDone();
    } else {
      PlayerResource.getPlayersInSeasonTeam({ seasonUniqid: tournamentInfo.uniqid, seasonTeamUniqid: awayTeamUniqid })
        .then(({ data }) => {
          setAwayPlayers(data);
          apiDone();
        })
        .catch(() => {});
    }

    const homeTeamUniqid = game.home.season_team_uniqid;
    if (!homeTeamUniqid) {
      apiDone();
    } else {
      PlayerResource.getPlayersInSeasonTeam({ seasonUniqid: tournamentInfo.uniqid, seasonTeamUniqid: homeTeamUniqid })
        .then(({ data }) => {
          setHomePlayers(data);
          apiDone();
        })
        .catch(() => {});
    }
  }, [game.away.season_team_uniqid, game.home.season_team_uniqid, game.uniqid, tournamentInfo.uniqid]);

  useEffect(() => {
    if (!game || !game.related_links) {
      setRelatedLinks([]);
      return;
    }

    setRelatedLinks(game.related_links);
  }, [game]);

  useEffect(() => {
    setEditData(null);
  }, [part]);

  const cancelEditing = () => {
    const currentSearchValue = qs.parse(urlLocation.search, { ignoreQueryPrefix: true }) || {};
    currentSearchValue.part = 'overview';

    history.push(`${urlLocation.pathname}?${qs.stringify(currentSearchValue)}`);
  };

  const handleEditSubmit = () => {
    if (!editData) {
      return;
    }

    let statsSide = 'HOME';
    let targetMethod = null;
    let targetData = null;
    let afterSuccess = () => {};
    switch (part) {
      case 'score_board': {
        const { error, message, data: validData } = validScoreBoardData(
          tournamentInfo.auto_game_seq,
          games,
          game.seq,
          editData,
        );
        if (error) {
          toast(message, { status: 'error' });
          return;
        }
        targetMethod = updateGameScoreBoard;
        targetData = validData;
        afterSuccess = (newGameData) => {
          tournamentDispatch({
            type: 'UPDATE_GAME',
            payload: {
              ...newGameData,
            },
          });
        };
        break;
      }
      case 'media':
        targetMethod = updateGameMedia;
        targetData = editData;
        afterSuccess = (newMediaList) => {
          tournamentDispatch({
            type: 'UPDATE_GAME',
            payload: {
              ...game,
              media: newMediaList,
            },
          });
        };
        break;
      case 'news':
        if (!editData.title && !editData.image_path && !editData.content && game.news) {
          targetMethod = deleteGameNews;
          targetData = editData;
          afterSuccess = () => {
            tournamentDispatch({
              type: 'UPDATE_GAME',
              payload: {
                ...game,
                news: undefined,
              },
            });
          };
        } else if (!editData.title || !editData.image_path || !editData.content) {
          toast(Multilingual(`TOURNAMENT.ERROR.GAME_NEWS_REQUIREMENT`), { status: 'error' });
          return;
        } else {
          targetMethod = updateGameNews;
          targetData = editData;
          afterSuccess = (newNews) => {
            tournamentDispatch({
              type: 'UPDATE_GAME',
              payload: {
                ...game,
                news: newNews,
              },
            });
          };
        }
        break;
      case 'related_links':
        targetMethod = updateGameRelatedLinks;
        targetData = editData;
        afterSuccess = (newRelatedLinks) => {
          tournamentDispatch({
            type: 'UPDATE_GAME',
            payload: {
              ...game,
              related_links: newRelatedLinks,
            },
          });
        };
        break;
      case 'away_stats':
        statsSide = 'AWAY';
      // fallthrough
      case 'home_stats': {
        const { error, message, data: validData } = validStatsData(
          editData,
          statsSide === 'AWAY' ? gameWithPlayers.away.players : gameWithPlayers.home.players,
        );
        if (error) {
          toast(message, { status: 'error' });
          return;
        }
        targetData = {
          ...validData,
          side: statsSide,
        };
        targetMethod = updateGameStats;
        afterSuccess = (newGameDataWithPlayers) => {
          tournamentDispatch({
            type: 'UPDATE_GAME',
            payload: {
              ...newGameDataWithPlayers,
            },
          });
        };
        break;
      }
      default:
        return;
    }

    if (!targetMethod) {
      return;
    }

    setEditApiInProgress(true);
    targetMethod(tournamentInfo.uniqid, game.uniqid, targetData)
      .then((responseData) => {
        toast(Multilingual(`SUCCESS.UPDATE`), { status: 'success' });
        afterSuccess(responseData);
      })
      .catch(() => {
        toast(Multilingual(`TOURNAMENT.ERROR.UNKNOWN`), { status: 'error' });
      })
      .finally(() => {
        setEditApiInProgress(false);
        cancelEditing();
      });
  };

  const partLink = (targetPart) => {
    if (editData && part !== targetPart) {
      return (
        <label className={`section-list--item disabled ${targetPart}`}>
          {Multilingual(`GAME.SIDEBAR.${targetPart.toUpperCase()}`)}
        </label>
      );
    }

    return (
      <Link
        className={`section-list--item ${targetPart} ${part === targetPart && 'selected'}`}
        to={(location) => {
          const currentSearchValue = qs.parse(location.search, { ignoreQueryPrefix: true }) || {};
          currentSearchValue.part = targetPart;
          return {
            ...location,
            search: qs.stringify(currentSearchValue),
          };
        }}
      >
        {Multilingual(`GAME.SIDEBAR.${targetPart.toUpperCase()}`)}
      </Link>
    );
  };

  const getSection = () => {
    switch (part) {
      case 'overview':
        return <GameOverviewSection game={gameWithPlayers} playerLoading={playerLoading} />;
      case 'score_board':
        return <GameScoreBoardSection game={game} onChange={setEditData} apiInProgress={editApiInProgress} />;
      case 'media':
        return (
          <GameMediaSection
            isPremium={tournamentInfo.config.expand_media_space}
            mediaList={!game.media ? [] : game.media}
            onChange={setEditData}
            apiInProgress={editApiInProgress}
          />
        );
      case 'news':
        return (
          <GameNewsSection
            news={!game.news ? null : game.news}
            onChange={setEditData}
            apiInProgress={editApiInProgress}
          />
        );
      case 'related_links':
        return (
          <GameRelatedLinksSection
            relatedLinks={relatedLinks}
            onChange={setEditData}
            apiInProgress={editApiInProgress}
          />
        );
      case 'away_stats':
        return (
          <GameStatsSection
            key="away"
            title={Multilingual(`GAME.SIDEBAR.AWAY_STATS`)}
            playerLoading={playerLoading}
            records={gameWithPlayers.away.records}
            batters={gameWithPlayers.away.batters}
            pitchers={gameWithPlayers.away.pitchers}
            inGameplayers={gameWithPlayers.away.players}
            teamPlayers={awayPlayers}
            recordPbP={gameWithPlayers.away.record_pbp}
            onChange={setEditData}
            apiInProgress={editApiInProgress}
          />
        );
      case 'home_stats':
        return (
          <GameStatsSection
            key="home"
            title={Multilingual(`GAME.SIDEBAR.HOME_STATS`)}
            playerLoading={playerLoading}
            records={gameWithPlayers.home.records}
            batters={gameWithPlayers.home.batters}
            pitchers={gameWithPlayers.home.pitchers}
            inGameplayers={gameWithPlayers.home.players}
            teamPlayers={homePlayers}
            recordPbP={gameWithPlayers.home.record_pbp}
            onChange={setEditData}
            apiInProgress={editApiInProgress}
          />
        );
      case 'platform_admin_only':
        if (!isPlatformAdmin) {
          return <></>;
        }
        return (
          <GameAdminSection
            awayTeamPlayers={awayPlayers}
            homeTeamPlayers={homePlayers}
            gameWithPlayers={gameWithPlayers}
          />
        );
      case 'org_G66N_only':
        if (!isPlatformAdmin || organizer.uniqid !== 'G66N') {
          return <></>;
        }
        return (
          <GameG66NSection
            awayTeamPlayers={awayPlayers}
            homeTeamPlayers={homePlayers}
            gameWithPlayers={gameWithPlayers}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <StyledDiv>
      <div className="greet">
        {organizer.logo_url ? (
          <img className="greet--pic" src={organizer.logo_url} alt="" />
        ) : (
          <div className="greet--pic default"></div>
        )}
        <div className="greet--name">
          {organizer.name}
          {' - '}
          {tournamentInfo.title}
        </div>
      </div>
      <div className="panel">
        <div className="section-list">
          <div className="section-list--title"></div>
          {editData ? (
            <label
              className={`section-list--item ${editApiInProgress && `disabled`}`}
              onClick={editApiInProgress ? () => {} : cancelEditing}
            >
              {Multilingual(`COMMON.CANCEL`)}
            </label>
          ) : (
            <Link
              className="section-list--item"
              to={() => ({
                pathname: `/organizer/${organizer.uniqid}/tournament/${tournamentInfo.uniqid}/schedule`,
              })}
            >
              <StyledBackIcon />
              {Multilingual(`GAME.SIDEBAR.BACK`)}
            </Link>
          )}
          {partLink('overview')}
          {partLink('score_board')}
          {partLink('media')}
          {partLink('news')}
          {partLink('related_links')}
          {game.info.status === 'FINISHED' && (
            <Fragment>
              {partLink('away_stats')}
              {partLink('home_stats')}
            </Fragment>
          )}
          {isPlatformAdmin && partLink('platform_admin_only')}
          {isPlatformAdmin && organizer.uniqid === 'G66N' && partLink('org_G66N_only')}
        </div>
        <div className={`section ${['away_stats', 'home_stats'].includes(part) && 'lg'}`}>
          <div className="section--title">
            <a
              href={`${process.env.REACT_APP_MAINPAGE_URL}/season/${tournamentInfo.uniqid}?game=${game.uniqid}&tab=box`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {gameTitle}
            </a>
          </div>
          {editData && (
            <div className="section--subtitle">
              {Multilingual(`FORM.UNSAVED_CONTENT_EXIST`)}
              <Button width="unset" color="red" onClick={handleEditSubmit} disabled={editApiInProgress}>
                {Multilingual(`COMMON.UPDATE`)}
              </Button>
            </div>
          )}
          {getSection()}
          {editData && (
            <div className="section--footer">
              <Button width="unset" color="red" onClick={handleEditSubmit} disabled={editApiInProgress}>
                {Multilingual(`COMMON.UPDATE`)}
              </Button>
            </div>
          )}
        </div>
      </div>
    </StyledDiv>
  );
};

export default Game;
